// TES-1093: „Mängel-Inbox" — rollenspezifisches Dashboard für mangelbearbeiter.
// Owner-Matrix (2026-06-11): mir zugewiesene Mängel nach Schwere/Alter sortiert,
// Aging-Hinweise, kürzlich gelöste — KEINE Prüfungs-Sektionen (Bestand:
// dashboard-stats.spec asserted die Abwesenheit von data-tour="dashboard-inspections").
// Wiederverwendet S1-Bausteine (KpiStatCard, StatCard, SeverityBadge); Daten
// user-scoped, RLS bleibt die Sicherheitsgrenze.
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { formatDistanceToNow } from "date-fns";
import type { Locale } from "date-fns";
import { AlertTriangle, CheckCircle2, ChevronRight, Clock, Inbox, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DefectStatusBadge, SeverityBadge } from "@/components/ui/status-badge";
import { PageContainer } from "@/components/layout/PageContainer";
import { DashboardSkeleton } from "@/components/dashboard/DashboardSkeleton";
import { MaengelOhneBearbeiterCard } from "@/components/dashboard/MaengelOhneBearbeiterCard";
import {
  RoleDashboardHeader,
  RoleKpiRow,
  RoleTileSection,
  type RoleKpiItem,
} from "@/components/dashboard/RoleDashboardSections";
import { resolveDateLocale } from "@/components/dashboard/dashboardBento.helpers";
import type { InboxDefect, InboxKpis } from "@/components/dashboard/roleDashboard.helpers";
import { useDefectInboxQuery } from "@/hooks/useDefectInboxQuery";
import { useRoleDashboardData } from "@/hooks/useRoleDashboardData";
import { isDateOverdue, formatSafeDate, DATE_FORMATS } from "@/lib/dateUtils";
import { cn } from "@/lib/utils";
import type { DefectSeverity, DefectStatus } from "@/types";

const INBOX_LIST_LIMIT = 8;

/** Inbox-KPIs (Mir zugewiesen / Überfällig / Kritisch-Hoch / In Bearbeitung). */
function buildInboxKpiItems(kpis: InboxKpis, t: TFunction): RoleKpiItem[] {
  return [
    {
      testId: "inbox-kpi-assigned",
      label: t("dashboard.inbox.assignedOpen"),
      value: kpis.assignedOpen,
      icon: Inbox,
      tone: "primary",
    },
    {
      testId: "inbox-kpi-overdue",
      label: t("dashboard.overdue"),
      value: kpis.overdue,
      icon: Clock,
      tone: "error",
    },
    {
      testId: "inbox-kpi-high-severity",
      label: t("dashboard.inbox.highSeverity"),
      value: kpis.highSeverity,
      icon: AlertTriangle,
      tone: "error",
    },
    {
      testId: "inbox-kpi-in-progress",
      label: t("dashboard.inProgress"),
      value: kpis.inProgress,
      icon: Play,
      tone: "secondary",
    },
  ];
}

/** Eine Inbox-Zeile: Schwere-Badge + Titel + Aging-Hinweis, überfällig = roter Akzent. */
function InboxRow({
  defect,
  onOpen,
  dateLocale,
}: {
  defect: InboxDefect;
  onOpen: () => void;
  dateLocale: Locale;
}) {
  const { t } = useTranslation();
  const overdue = isDateOverdue(defect.due_date);
  const age = formatDistanceToNow(new Date(defect.created_at), { locale: dateLocale });

  return (
    <button
      type="button"
      data-testid={`inbox-row-${defect.id}`}
      onClick={onOpen}
      className={cn(
        "w-full p-4 bg-surface rounded border-l-4 flex justify-between items-center gap-3 group cursor-pointer hover:bg-surface-container-low transition-colors text-left",
        overdue ? "border-destructive" : "border-outline-variant",
      )}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <SeverityBadge severity={defect.severity as DefectSeverity} />
          {overdue && (
            <span className="font-bold text-label-caps text-destructive">
              {t("dashboard.overdue")}
            </span>
          )}
        </div>
        <p className="text-sm text-foreground mt-2 truncate font-medium">{defect.title}</p>
        <p className="text-data-tabular text-muted-foreground mt-0.5">
          {t("dashboard.inbox.openFor", { age })}
        </p>
      </div>
      <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:translate-x-1 transition-transform shrink-0" />
    </button>
  );
}

/** Inbox-Panel: mir zugewiesene offene Mängel nach Schwere/Alter. */
function InboxListPanel({ open, dateLocale }: { open: InboxDefect[]; dateLocale: Locale }) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div
      className="@4xl:col-span-2 bg-surface-container-lowest border border-outline-variant rounded p-6"
      data-testid="inbox-list"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2">
          <Inbox className="h-5 w-5 text-primary" />
          {t("dashboard.inbox.listTitle")}
        </h3>
        <Button
          variant="link"
          onClick={() => navigate("/defects")}
          className="text-primary font-bold text-label-caps p-0 h-auto hover:underline"
        >
          {t("dashboard.inbox.viewAllDefects")}
        </Button>
      </div>
      {open.length === 0 ? (
        <div
          className="flex flex-col items-center justify-center py-10 text-muted-foreground"
          data-testid="inbox-empty"
        >
          <CheckCircle2 className="h-10 w-10 mb-2 text-success" />
          <p className="text-sm">{t("dashboard.inbox.empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {open.slice(0, INBOX_LIST_LIMIT).map((defect) => (
            <InboxRow
              key={defect.id}
              defect={defect}
              onOpen={() => navigate(`/defects?id=${defect.id}`)}
              dateLocale={dateLocale}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Panel „Kürzlich gelöst" — die zuletzt gelösten/geschlossenen eigenen Mängel. */
function RecentlyResolvedPanel({
  recentlyResolved,
  dateLocale,
}: {
  recentlyResolved: InboxDefect[];
  dateLocale: Locale;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div
      className="bg-surface-container-lowest border border-outline-variant rounded p-6"
      data-testid="inbox-recently-resolved"
    >
      <h3 className="font-display text-lg font-semibold flex items-center gap-2 mb-6">
        <CheckCircle2 className="h-5 w-5 text-success" />
        {t("dashboard.inbox.recentlyResolved")}
      </h3>
      {recentlyResolved.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="inbox-resolved-empty">
          {t("dashboard.inbox.recentlyResolvedEmpty")}
        </p>
      ) : (
        <div className="space-y-3">
          {recentlyResolved.map((defect) => (
            <button
              key={defect.id}
              type="button"
              data-testid={`inbox-resolved-row-${defect.id}`}
              onClick={() => navigate(`/defects?id=${defect.id}`)}
              className="w-full p-3 bg-surface rounded flex justify-between items-center gap-3 group cursor-pointer hover:bg-surface-container-low transition-colors text-left"
            >
              <div className="min-w-0">
                <p className="text-sm text-foreground truncate font-medium">{defect.title}</p>
                <p className="text-data-tabular text-muted-foreground mt-0.5">
                  {formatSafeDate(defect.resolved_at ?? defect.updated_at, DATE_FORMATS.DATE, {
                    locale: dateLocale,
                  })}
                </p>
              </div>
              <DefectStatusBadge status={defect.status as DefectStatus} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MangelbearbeiterDashboard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dateLocale = resolveDateLocale(i18n.language);
  const { userId, userName, showSkeleton, defectTiles } = useRoleDashboardData({
    includeUnassigned: false,
  });
  const { open, recentlyResolved, kpis } = useDefectInboxQuery(userId);

  return (
    <PageContainer className="space-y-8" data-testid="inbox-dashboard">
      <RoleDashboardHeader
        userName={userName}
        title={t("dashboard.inbox.title")}
        subtitle={t("dashboard.inbox.subtitle")}
        actions={
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/defects")}
              className="border-primary text-primary font-bold hover:bg-surface-container-low gap-2"
            >
              <AlertTriangle className="h-4 w-4" />
              <span className="text-label-caps">{t("dashboard.inbox.viewAllDefects")}</span>
            </Button>
          </div>
        }
      />

      {/* Inbox-KPI-Zeile */}
      <RoleKpiRow items={buildInboxKpiItems(kpis, t)} testId="inbox-kpi-row" />

      {/* TES-1863: ownerlose Mängel der eigenen Fachbereich-Gruppen (rendert null, wenn keine vorhanden). */}
      <MaengelOhneBearbeiterCard />

      <div className="grid grid-cols-1 @4xl:grid-cols-3 gap-6 items-start">
        <InboxListPanel open={open} dateLocale={dateLocale} />
        <RecentlyResolvedPanel recentlyResolved={recentlyResolved} dateLocale={dateLocale} />
      </div>

      {showSkeleton ? (
        <DashboardSkeleton />
      ) : (
        /* Meine Mängel — Status-Kacheln (testIds wie Dashboard.tsx, Bestand counter-updates) */
        <RoleTileSection
          title={t("dashboard.defects")}
          icon={AlertTriangle}
          iconClassName="h-5 w-5 text-warning"
          tiles={defectTiles}
          dataTour="dashboard-defects"
        />
      )}
    </PageContainer>
  );
}
